export type TextDecorationValue = 'none' | 'line-through' | 'overline' | 'underline'

export type TextTransformValue = 'none' | 'uppercase' | 'lowercase' | 'capitalize'

export type BgImageSizeValue = 'auto' | 'contain' | 'cover' | 'advanced'

export type BgPositionValue =
  | 'advanced'
  | 'center center'
  | 'center top'
  | 'center bottom'
  | 'left center'
  | 'left top'
  | 'left bottom'
  | 'right center'
  | 'right top'
  | 'right bottom'

export const bgPositionToValue = (pos: BgPositionValue): string => {
  switch (pos) {
    case 'center center':
      return '50%'
    case 'center top':
      return 'top'
    case 'center bottom':
      return 'bottom'
    case 'left center':
      return '0'
    case 'left top':
      return '0 0'
    case 'left bottom':
      return '0 100%'
    case 'right center':
      return '100%'
    case 'right top':
      return '100% 0'
    case 'right bottom':
      return '100% 100%'
    default:
      return pos
  }
}

export type WidthValue = 'auto' | 'fixed' | 'percent' | number

export const widthToValue = (width: WidthValue, fixed?: number, percent?: number): string => {
  if (typeof width === 'number') {
    return `${width}%`
  }

  switch (width) {
    case 'auto':
      return 'auto'
    case 'fixed':
      return `${fixed}px`
    case 'percent':
      return `${percent}%`
    default:
      return width
  }
}

export type AlignValue = 'left' | 'center' | 'right'

const stylesMap = {
  columnGap: (o: { column_gap: number }) => `${o.column_gap}px`,
  width: (o: { width: WidthValue; width_fixed: number; width_percent: number }) =>
    widthToValue(o.width, o.width_fixed, o.width_percent),
  marginTop: (o: { margin_top: number }) => o.margin_top,
  marginLeft: (o: { align: AlignValue }) => (o.align === 'left' ? '0' : 'auto'),
  marginRight: (o: { align: AlignValue }) => (o.align === 'right' ? '0' : 'auto'),
  paddingTop: (o: { padding_top: number }) => o.padding_top,
  paddingRight: (o: { padding_right: number }) => o.padding_right,
  paddingBottom: (o: { padding_bottom: number }) => o.padding_bottom,
  paddingLeft: (o: { padding_left: number }) => o.padding_left,
  border: (o: { border_size: number; border_style: string; border_color: string }) =>
    `${o.border_size}px ${o.border_style} rgba(${o.border_color})`,
  borderTop: (o: { border_side: boolean[]; border_size: number; border_style: string; border_color: string }) =>
    o.border_side?.[0] ? `${o.border_size}px ${o.border_style} rgba(${o.border_color})` : 'none',
  borderRight: (o: { border_side: boolean[]; border_size: number; border_style: string; border_color: string }) =>
    o.border_side?.[1] ? `${o.border_size}px ${o.border_style} rgba(${o.border_color})` : 'none',
  borderBottom: (o: { border_side: boolean[]; border_size: number; border_style: string; border_color: string }) =>
    o.border_side?.[2] ? `${o.border_size}px ${o.border_style} rgba(${o.border_color})` : 'none',
  borderLeft: (o: { border_side: boolean[]; border_size: number; border_style: string; border_color: string }) =>
    o.border_side?.[3] ? `${o.border_size}px ${o.border_style} rgba(${o.border_color})` : 'none',
  borderTopLeftRadius: (o: { border_top_left: number }) => o.border_top_left,
  borderTopRightRadius: (o: { border_top_right: number }) => o.border_top_right,
  borderBottomRightRadius: (o: { border_bottom_right: number }) => o.border_bottom_right,
  borderBottomLeftRadius: (o: { border_bottom_left: number }) => o.border_bottom_left,
  opacity: (o: { opacity: number }) => o.opacity,
  backgroundColor: (o: { bg_color: string }) => `rgba(${o.bg_color})`,
  backgroundImage: (o: { bg_image_url: string | null }) => (o.bg_image_url ? `url(${o.bg_image_url})` : null),
  backgroundPosition: (o: { bg_image_position: BgPositionValue; bg_image_position_advanced: string }) =>
    o.bg_image_position === 'advanced' ? o.bg_image_position_advanced : bgPositionToValue(o.bg_image_position),
  backgroundSize: (o: { bg_image_size: BgImageSizeValue; bg_image_size_advanced: string }) =>
    o.bg_image_size === 'advanced' ? o.bg_image_size_advanced : o.bg_image_size,
  color: (o: { text_color: string }) => `rgba(${o.text_color})`,
  textAlign: (o: { text_align: string }) => o.text_align,
  lineHeight: (o: { line_height: number }) => o.line_height,
  textTransform: (o: { text_transform: TextTransformValue }) => o.text_transform,
  textDecoration: (o: { text_decoration: TextDecorationValue }) => o.text_decoration,
  letterSpacing: (o: { letter_spacing: string }) => o.letter_spacing,
  textShadow: (o: { text_shadow: string }) => o.text_shadow,
  fontFamily: (o: { font_family: string }) => o.font_family,
  fontSize: (o: { font_size: number }) => o.font_size,
  fontWeight: (o: { font_weight: string }) => o.font_weight,
  boxShadow: (o: { box_shadow: string }) => o.box_shadow
}

type UnionToIntersection<U> = (U extends any ? (k: U) => void : never) extends (k: infer I) => void ? I : never

export type TypesMap = typeof stylesMap

export type Options<T extends keyof TypesMap> = UnionToIntersection<Parameters<TypesMap[T]>[0]>

export const getStylesFn =
  <T extends keyof TypesMap>(styleProps: T[], defaultStyles: Record<string, string | number> = {}) =>
  (options: Options<T>) => {
    const result: Partial<Record<keyof TypesMap, any>> = {}

    styleProps.forEach(prop => {
      const styleFn = stylesMap[prop]

      const resValue = styleFn(options as any)
      if (resValue !== undefined && resValue !== null) {
        result[prop] = resValue
      }
    })

    return {
      ...defaultStyles,
      ...result
    }
  }
